import {
  Search,
  Plus,
  Minus,
  Trash2,
  X,
  Check,
  CreditCard,
  Banknote,
  DollarSign,
  Truck,
  User,
  Users,
  AlertCircle,
  Key,
  LogOut,
  Printer,
  FileText,
  Calendar,
  Building2,
  CheckSquare,
  LogIn,
  Phone,
  MapPin,
} from 'lucide-react'
import { useState } from 'react'

import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Checkbox } from '../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { Textarea } from '../components/ui/textarea'

interface Product {
  id: number
  name: string
  code: string
  price: number
  stock: number
  stockCentro: number
  stockNorte: number
  stockSur: number
  category: string
  requiresPrescription: boolean
  productType: 'controlled' | 'antibiotic' | 'otc'
  image: string
  promotion?: string
  promotionDiscount?: number
  promotionExpiry?: string
}

interface CartItem extends Product {
  quantity: number
  selected: boolean
}

interface PrescriptionData {
  doctorId?: number
  doctorName: string
  doctorAddress: string
  professionalId: string
  prescriptionDate: string
  saleDate: string
  fulfillmentType: 'partial' | 'complete'
  folio?: string
}

interface Doctor {
  id: number
  name: string
  professionalId: string
  address: string
  specialty: string
}

interface DeliveryDetails {
  deliveryPerson: string
  customerPhone: string
  customerAddress: string
  observations: string
  deliveryCost: number
}

const products: Product[] = [
  {
    id: 1,
    name: 'Paracetamol 500mg',
    code: 'MED-001',
    price: 45.0,
    stock: 250,
    stockCentro: 250,
    stockNorte: 180,
    stockSur: 200,
    category: 'Analgésicos',
    requiresPrescription: false,
    productType: 'otc',
    image:
      'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=200&h=200&fit=crop',
    promotion: '3x2',
    promotionExpiry: '2026-05-15',
  },
  {
    id: 2,
    name: 'Ibuprofeno 400mg',
    code: 'MED-002',
    price: 65.0,
    stock: 180,
    stockCentro: 180,
    stockNorte: 150,
    stockSur: 160,
    category: 'Analgésicos',
    requiresPrescription: false,
    productType: 'otc',
    image:
      'https://images.unsplash.com/photo-1471864190281-a93a3070b6de?w=200&h=200&fit=crop',
    promotionDiscount: 15,
    promotionExpiry: '2026-05-20',
  },
  {
    id: 3,
    name: 'Amoxicilina 500mg',
    code: 'MED-003',
    price: 180.0,
    stock: 95,
    stockCentro: 95,
    stockNorte: 80,
    stockSur: 100,
    category: 'Antibióticos',
    requiresPrescription: true,
    productType: 'antibiotic',
    image:
      'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=200&h=200&fit=crop',
  },
  {
    id: 4,
    name: 'Loratadina 10mg',
    code: 'MED-004',
    price: 120.0,
    stock: 140,
    stockCentro: 140,
    stockNorte: 120,
    stockSur: 130,
    category: 'Antihistamínicos',
    requiresPrescription: false,
    productType: 'otc',
    image:
      'https://images.unsplash.com/photo-1550572017-4e6c5b13fe49?w=200&h=200&fit=crop',
  },
  {
    id: 5,
    name: 'Losartán 50mg',
    code: 'MED-005',
    price: 150.0,
    stock: 160,
    stockCentro: 160,
    stockNorte: 140,
    stockSur: 150,
    category: 'Cardiovascular',
    requiresPrescription: true,
    productType: 'controlled',
    image:
      'https://images.unsplash.com/photo-1585435557343-3b092031a831?w=200&h=200&fit=crop',
  },
]

const customers = [
  { id: 1, name: 'María González', phone: '555-1234', loyaltyPoints: 1560 },
  { id: 2, name: 'Carlos Méndez', phone: '555-5678', loyaltyPoints: 890 },
  { id: 3, name: 'Ana Torres', phone: '555-9012', loyaltyPoints: 125 },
]

const deliveryPersons = [
  { id: 1, name: 'Juan Pérez' },
  { id: 2, name: 'Roberto López' },
  { id: 3, name: 'Sofia García' },
]

const doctors: Doctor[] = [
  {
    id: 1,
    name: 'Dr. Carlos Ramírez',
    professionalId: '1234567',
    address: 'Consultorio 101, Centro Médico',
    specialty: 'Medicina General',
  },
  {
    id: 2,
    name: 'Dra. María Fernández',
    professionalId: '7654321',
    address: 'Hospital Regional, 3er piso',
    specialty: 'Pediatría',
  },
  {
    id: 3,
    name: 'Dr. Roberto Sánchez',
    professionalId: '9876543',
    address: 'Av. Insurgentes 450',
    specialty: 'Cardiología',
  },
]

const CASH_LIMIT = 5000
const CASH_WARNING = 4000
const POINT_VALUE = 1 // 1 punto = $1

export function PointOfSale() {
  const [searchTerm, setSearchTerm] = useState('')
  const [cart, setCart] = useState<CartItem[]>([])
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showTicketPreview, setShowTicketPreview] = useState(false)
  const [showCashWithdrawal, setShowCashWithdrawal] = useState(false)
  const [showCashCount, setShowCashCount] = useState(false)
  const [showCashCountResult, setShowCashCountResult] = useState(false)
  const [showChangeCashier, setShowChangeCashier] = useState(false)
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false)
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false)
  const [showDeliveryDetailsModal, setShowDeliveryDetailsModal] =
    useState(false)
  const [showCashDeposit, setShowCashDeposit] = useState(false)
  const [showNewDoctorModal, setShowNewDoctorModal] = useState(false)

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | null
  >(null)
  const [cashReceived, setCashReceived] = useState('')
  const [currentCashInRegister, setCurrentCashInRegister] = useState(2500)
  const [selectedCardBank, setSelectedCardBank] = useState('')
  const [cashierNumber, setCashierNumber] = useState(1)
  const [cashierName, setCashierName] = useState('Juan Pérez')
  const [usePoints, setUsePoints] = useState(false)
  const [pointsToUse, setPointsToUse] = useState(0)
  const [adminPin, setAdminPin] = useState('')

  // Sale type
  const [saleType, setSaleType] = useState<'regular' | 'delivery'>('regular')
  const [selectedDeliveryPerson, setSelectedDeliveryPerson] = useState('')
  const [deliveryNotes, setDeliveryNotes] = useState('')

  // Customer
  const [customerType, setCustomerType] = useState<'general' | 'registered'>(
    'general',
  )
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null)

  // Credit payment
  const [creditTermDays, setCreditTermDays] = useState('')
  const [creditPayments, setCreditPayments] = useState('')

  // Invoice
  const [requiresInvoice, setRequiresInvoice] = useState(false)
  const [ticketCopies, setTicketCopies] = useState(1)

  // Bank for transfer
  const [selectedBank, setSelectedBank] = useState('')

  // Cash count
  const [countedCash, setCountedCash] = useState('')
  const [denominations, setDenominations] = useState({
    1: 0,
    2: 0,
    5: 0,
    10: 0,
    20: 0,
    50: 0,
    100: 0,
    200: 0,
    500: 0,
    1000: 0,
  })
  const [withdrawalAmount, setWithdrawalAmount] = useState('')
  const [withdrawalConcept, setWithdrawalConcept] = useState('')
  const [depositAmount, setDepositAmount] = useState('')
  const [depositConcept, setDepositConcept] = useState('')
  const [cashCountDifference, setCashCountDifference] = useState(0)
  const [cashCountStatus, setCashCountStatus] = useState<
    'success' | 'surplus' | 'shortage'
  >('success')

  // Prescription
  const [currentPrescriptionProduct, setCurrentPrescriptionProduct] = useState<
    number | null
  >(null)
  const [prescriptionData, setPrescriptionData] = useState<
    Record<number, PrescriptionData>
  >({})
  const [prescriptionFolioCounters, setPrescriptionFolioCounters] = useState({
    controlled: 1,
    antibiotic: 1,
  })
  const [generatedFolio, setGeneratedFolio] = useState('')
  const [manualFolio, setManualFolio] = useState('')
  const [selectedDoctor, setSelectedDoctor] = useState<number | null>(null)
  const [doctorSearch, setDoctorSearch] = useState('')

  // Delivery details
  const [deliveryDetails, setDeliveryDetails] = useState<DeliveryDetails>({
    deliveryPerson: '',
    customerPhone: '',
    customerAddress: '',
    observations: '',
    deliveryCost: 0,
  })

  // Ticket info
  const [ticketNumber, setTicketNumber] = useState('')

  const filteredProducts = products.filter(
    (p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.code.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const addToCart = (product: Product) => {
    // Check if product requires prescription data
    if (
      product.productType === 'controlled' ||
      product.productType === 'antibiotic'
    ) {
      setCurrentPrescriptionProduct(product.id)
      setShowPrescriptionModal(true)
      return
    }

    addProductToCart(product)
  }

  const addProductToCart = (product: Product) => {
    const existingItem = cart.find((item) => item.id === product.id)
    if (existingItem) {
      setCart(
        cart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        ),
      )
    } else {
      setCart([...cart, { ...product, quantity: 1, selected: false }])
    }
  }

  const updateQuantity = (id: number, delta: number) => {
    setCart(
      cart
        .map((item) => {
          if (item.id === id) {
            const newQuantity = item.quantity + delta
            return newQuantity > 0 ? { ...item, quantity: newQuantity } : item
          }
          return item
        })
        .filter((item) => item.quantity > 0),
    )
  }

  const removeFromCart = (id: number) => {
    setCart(cart.filter((item) => item.id !== id))
  }

  const toggleSelectItem = (id: number) => {
    setCart(
      cart.map((item) =>
        item.id === id ? { ...item, selected: !item.selected } : item,
      ),
    )
  }

  const deleteSelectedItems = () => {
    setCart(cart.filter((item) => !item.selected))
  }

  const subtotal = cart.reduce((sum, item) => {
    let itemPrice = item.price
    if (item.promotionDiscount) {
      itemPrice = item.price * (1 - item.promotionDiscount / 100)
    }
    return sum + itemPrice * item.quantity
  }, 0)

  const tax = subtotal * 0.16
  const deliveryCost =
    saleType === 'delivery' ? deliveryDetails.deliveryCost : 0
  const totalBeforePoints = subtotal + tax + deliveryCost

  // Calcular puntos adquiridos (10% del total sin costo de envío)
  const pointsEarned = Math.floor((subtotal + tax) * 0.1)

  // Calcular descuento por puntos (1 punto = $1)
  const pointsDiscount = usePoints
    ? Math.min(pointsToUse, totalBeforePoints)
    : 0
  const total = totalBeforePoints - pointsDiscount

  // Obtener puntos disponibles del cliente seleccionado
  const customerPoints = selectedCustomer
    ? customers.find((c) => c.id === selectedCustomer)?.loyaltyPoints || 0
    : 0

  const generateTicketNumber = () => {
    let prefix = ''

    if (selectedPaymentMethod === 'transfer') {
      prefix = `${selectedBank}-`
    } else if (selectedPaymentMethod === 'card') {
      prefix = `${selectedCardBank}-`
    } else if (selectedPaymentMethod === 'cash') {
      prefix = 'EF-'
    } else if (selectedPaymentMethod === 'credit') {
      prefix = 'CR-'
    } else {
      prefix = 'VT-'
    }

    return `${prefix}${Math.floor(Math.random() * 9000) + 1000}`
  }

  const proceedToPayment = () => {
    // Check cash limit
    if (selectedPaymentMethod === 'cash') {
      const potentialTotal = currentCashInRegister + total
      if (potentialTotal > CASH_LIMIT) {
        alert(
          `No se puede procesar la venta. El límite de efectivo en caja es $${CASH_LIMIT.toFixed(2)} MXN`,
        )
        return
      }
    }

    const ticket = generateTicketNumber()
    setTicketNumber(ticket)
    setShowPaymentModal(false)
    setShowTicketPreview(true)
  }

  const completeSale = () => {
    if (selectedPaymentMethod === 'cash') {
      setCurrentCashInRegister(currentCashInRegister + total)
    }

    setShowTicketPreview(false)
    setShowSuccessModal(true)
    setTimeout(() => {
      setShowSuccessModal(false)
      setCart([])
      setSelectedPaymentMethod(null)
      setCashReceived('')
      setSaleType('regular')
      setCustomerType('general')
      setSelectedCustomer(null)
      setSelectedDeliveryPerson('')
      setDeliveryNotes('')
      setRequiresInvoice(false)
      setTicketCopies(1)
      setSelectedBank('')
      setSelectedCardBank('')
      setCreditTermDays('')
      setCreditPayments('')
      setDeliveryDetails({
        deliveryPerson: '',
        customerPhone: '',
        customerAddress: '',
        observations: '',
        deliveryCost: 0,
      })
      setUsePoints(false)
      setPointsToUse(0)
    }, 2000)
  }

  const handleCashWithdrawal = () => {
    const amount = parseFloat(withdrawalAmount)
    if (
      amount > 0 &&
      amount <= currentCashInRegister &&
      withdrawalConcept.trim()
    ) {
      setCurrentCashInRegister(currentCashInRegister - amount)
      setWithdrawalAmount('')
      setWithdrawalConcept('')
      setShowCashWithdrawal(false)
    }
  }

  const handleCashDeposit = () => {
    const amount = parseFloat(depositAmount)
    if (amount > 0 && depositConcept.trim()) {
      setCurrentCashInRegister(currentCashInRegister + amount)
      setDepositAmount('')
      setDepositConcept('')
      setShowCashDeposit(false)
    }
  }

  const calculateDenominationsTotal = (denom: typeof denominations) => {
    return Object.entries(denom).reduce((total, [value, quantity]) => {
      return total + parseFloat(value) * quantity
    }, 0)
  }

  const handleDenominationChange = (denomination: number, quantity: number) => {
    const newDenominations = { ...denominations, [denomination]: quantity }
    setDenominations(newDenominations)
    const total = calculateDenominationsTotal(newDenominations)
    setCountedCash(total.toString())
  }

  const handleCashCount = () => {
    const counted = parseFloat(countedCash)
    const difference = counted - currentCashInRegister

    setCashCountDifference(difference)

    if (difference === 0) {
      setCashCountStatus('success')
    } else if (difference > 0) {
      setCashCountStatus('surplus')
    } else {
      setCashCountStatus('shortage')
    }

    setShowCashCount(false)
    setShowCashCountResult(true)
  }

  const closeCashCountResult = () => {
    setShowCashCountResult(false)
    setCountedCash('')
    setCashCountDifference(0)
  }

  const handleChangeCashier = () => {
    if (adminPin === '1234') {
      const newCashier = parseInt(prompt('Ingrese el número de caja:') || '1')
      if (newCashier) {
        setCashierNumber(newCashier)
        setAdminPin('')
        setShowChangeCashier(false)
      }
    } else {
      alert('PIN incorrecto')
    }
  }

  const generatePrescriptionFolio = (
    productType: 'controlled' | 'antibiotic',
  ) => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')

    let prefix = ''
    let counter = 0

    if (productType === 'controlled') {
      prefix = 'MC' // Medicamento Controlado
      counter = prescriptionFolioCounters.controlled
    } else if (productType === 'antibiotic') {
      prefix = 'MA' // Medicamento Antibiótico
      counter = prescriptionFolioCounters.antibiotic
    }

    const folio = `${prefix}-${year}-${month}-${String(counter).padStart(3, '0')}`

    // Update counter
    setPrescriptionFolioCounters({
      ...prescriptionFolioCounters,
      [productType]: counter + 1,
    })

    return folio
  }

  const handleFulfillmentTypeChange = (isComplete: boolean) => {
    if (isComplete && currentPrescriptionProduct) {
      const product = products.find((p) => p.id === currentPrescriptionProduct)
      if (product) {
        // Solo generar automáticamente para antibióticos
        if (product.productType === 'antibiotic') {
          const folio = generatePrescriptionFolio(product.productType)
          setGeneratedFolio(folio)
          setManualFolio('')
        } else if (product.productType === 'controlled') {
          // Para controlados, limpiar el folio automático
          setGeneratedFolio('')
        }
      }
    } else {
      setGeneratedFolio('')
      setManualFolio('')
    }
  }

  const savePrescriptionData = () => {
    if (!currentPrescriptionProduct) return

    const product = products.find((p) => p.id === currentPrescriptionProduct)
    const isComplete = (
      document.getElementById('fulfillmentComplete') as HTMLInputElement
    )?.checked

    // Para controlados usar folio manual, para antibióticos usar folio generado
    const folioToUse =
      product?.productType === 'controlled' ? manualFolio : generatedFolio

    const data: PrescriptionData = {
      doctorName:
        (document.getElementById('doctorName') as HTMLInputElement)?.value ||
        '',
      doctorAddress:
        (document.getElementById('doctorAddress') as HTMLInputElement)?.value ||
        '',
      professionalId:
        (document.getElementById('professionalId') as HTMLInputElement)
          ?.value || '',
      prescriptionDate:
        (document.getElementById('prescriptionDate') as HTMLInputElement)
          ?.value || '',
      saleDate:
        (document.getElementById('saleDate') as HTMLInputElement)?.value || '',
      fulfillmentType: isComplete ? 'complete' : 'partial',
      folio: isComplete ? folioToUse : undefined,
    }

    setPrescriptionData({
      ...prescriptionData,
      [currentPrescriptionProduct]: data,
    })

    if (product) {
      addProductToCart(product)
    }

    setShowPrescriptionModal(false)
    setCurrentPrescriptionProduct(null)
    setGeneratedFolio('')
  }

  const change =
    selectedPaymentMethod === 'cash'
      ? parseFloat(cashReceived || '0') - total
      : 0
  const cashWarning =
    currentCashInRegister >= CASH_WARNING && currentCashInRegister < CASH_LIMIT
  const cashLimit = currentCashInRegister >= CASH_LIMIT

  return (
    <div className="flex h-screen flex-col bg-slate-50 lg:flex-row">
      {/* Left Side - Product Search */}
      <div className="flex flex-1 flex-col overflow-hidden p-6">
        {/* Header with cashier info */}
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-slate-900">
            Punto de Venta
          </h1>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2">
              <Building2 className="h-4 w-4 text-slate-600" />
              <span className="text-sm font-medium text-slate-900">
                Caja #{cashierNumber}
              </span>
              <button
                onClick={() => setShowChangeCashier(true)}
                className="ml-2 rounded p-1 hover:bg-slate-100"
              >
                <Key className="h-4 w-4 text-slate-600" />
              </button>
            </div>

            {/* Cash in register */}
            <div
              className={`rounded-lg border px-4 py-2 ${
                cashLimit
                  ? 'border-red-200 bg-red-50'
                  : cashWarning
                    ? 'border-yellow-200 bg-yellow-50'
                    : 'border-slate-200 bg-white'
              }`}
            >
              <div className="flex items-center gap-2">
                <Banknote
                  className={`h-4 w-4 ${
                    cashLimit
                      ? 'text-red-600'
                      : cashWarning
                        ? 'text-yellow-600'
                        : 'text-green-600'
                  }`}
                />
                <span className="text-sm font-semibold">
                  ${currentCashInRegister.toFixed(2)} MXN
                </span>
              </div>
            </div>

            <button
              onClick={() => setShowCashWithdrawal(true)}
              className="rounded-lg border border-slate-200 bg-white p-2 hover:bg-slate-50"
              title="Retiro de efectivo"
            >
              <LogOut className="h-4 w-4 text-red-600" />
            </button>

            <button
              onClick={() => setShowCashDeposit(true)}
              className="rounded-lg border border-slate-200 bg-white p-2 hover:bg-slate-50"
              title="Ingreso de efectivo"
            >
              <LogIn className="h-4 w-4 text-green-600" />
            </button>

            <button
              onClick={() => setShowCashCount(true)}
              className="rounded-lg bg-green-600 p-2 text-white hover:bg-green-700"
              title="Corte de caja"
            >
              <DollarSign className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Cash limit warning */}
        {cashWarning && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <p className="text-sm text-yellow-800">
              Advertencia: Se está acercando al límite de efectivo en caja ($
              {CASH_LIMIT.toFixed(2)} MXN)
            </p>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <Input
            type="text"
            placeholder="Buscar producto por nombre o código..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Products Grid */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {filteredProducts.map((product) => (
              <button
                key={product.id}
                onClick={() => addToCart(product)}
                className="rounded-lg border border-slate-200 bg-white p-4 text-left transition-all hover:border-green-500 hover:shadow-md"
              >
                {/* Product Image */}
                <div className="mb-3">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="h-32 w-32 rounded-lg object-cover"
                  />
                </div>

                <div className="mb-2 flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-slate-900">
                      {product.name}
                    </h3>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {product.code}
                    </p>
                  </div>
                  {product.requiresPrescription && (
                    <span className="ml-2 rounded bg-red-100 px-2 py-0.5 text-xs text-red-700">
                      Rx
                    </span>
                  )}
                </div>

                <p className="mb-2 text-xs text-slate-600">
                  {product.category}
                </p>

                {/* Promotion */}
                {(product.promotion || product.promotionDiscount) && (
                  <div className="mb-2 space-y-1">
                    {product.promotion && (
                      <div>
                        <Badge className="bg-purple-100 text-xs text-purple-700">
                          {product.promotion}
                        </Badge>
                      </div>
                    )}
                    {product.promotionDiscount && (
                      <div>
                        <Badge className="bg-orange-100 text-xs text-orange-700">
                          {product.promotionDiscount}% descuento
                        </Badge>
                      </div>
                    )}
                    {product.promotionExpiry && (
                      <p className="text-xs text-slate-600">
                        Válido hasta:{' '}
                        {new Date(product.promotionExpiry).toLocaleDateString(
                          'es-MX',
                        )}
                      </p>
                    )}
                  </div>
                )}

                <div className="mb-2 flex items-center justify-between">
                  <span className="font-semibold text-slate-900">
                    ${product.price.toFixed(2)}
                  </span>
                </div>

                {/* Stock by branch */}
                <div className="space-y-0.5 rounded bg-slate-50 p-2 text-xs text-slate-600">
                  <div className="flex justify-between">
                    <span>Centro:</span>
                    <span className="font-medium">{product.stockCentro}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Norte:</span>
                    <span className="font-medium">{product.stockNorte}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sur:</span>
                    <span className="font-medium">{product.stockSur}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Cart */}
      <div className="flex w-full flex-col border-l border-slate-200 bg-white lg:w-[450px]">
        <div className="space-y-3 border-b border-slate-200 p-4">
          <h2 className="font-semibold text-slate-900">Carrito de Venta</h2>

          {/* Sale Type */}
          <div className="flex gap-2">
            <button
              onClick={() => setSaleType('regular')}
              className={`flex-1 rounded-lg border-2 p-2 text-sm transition-all ${
                saleType === 'regular'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <User className="mx-auto mb-1 h-4 w-4" />
              <span className="text-xs font-medium">Venta en Tienda</span>
            </button>
            <button
              onClick={() => setSaleType('delivery')}
              className={`flex-1 rounded-lg border-2 p-2 text-sm transition-all ${
                saleType === 'delivery'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <Truck className="mx-auto mb-1 h-4 w-4" />
              <span className="text-xs font-medium">A Domicilio</span>
            </button>
          </div>

          {/* Delivery details */}
          {saleType === 'delivery' && (
            <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
              <Button
                onClick={() => setShowDeliveryDetailsModal(true)}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <Truck className="mr-2 h-4 w-4" />
                {deliveryDetails.deliveryPerson
                  ? 'Editar Datos de Entrega'
                  : 'Agregar Datos de Entrega'}
              </Button>
              {deliveryDetails.deliveryPerson && (
                <div className="space-y-1 rounded bg-white p-2 text-xs">
                  <p>
                    <strong>Repartidor:</strong>{' '}
                    {deliveryDetails.deliveryPerson}
                  </p>
                  <p>
                    <strong>Teléfono:</strong> {deliveryDetails.customerPhone}
                  </p>
                  <p>
                    <strong>Costo de envío:</strong> $
                    {deliveryDetails.deliveryCost.toFixed(2)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Customer selection */}
          <div className="flex gap-2">
            <button
              onClick={() => setCustomerType('general')}
              className={`flex-1 rounded-lg border px-3 py-1.5 text-xs transition-all ${
                customerType === 'general'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-slate-200'
              }`}
            >
              <Users className="mr-1 inline h-3 w-3" />
              Público General
            </button>
            <button
              onClick={() => setCustomerType('registered')}
              className={`flex-1 rounded-lg border px-3 py-1.5 text-xs transition-all ${
                customerType === 'registered'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-slate-200'
              }`}
            >
              <User className="mr-1 inline h-3 w-3" />
              Cliente Registrado
            </button>
          </div>

          {customerType === 'registered' && (
            <>
              <div className="flex gap-2">
                <Select
                  value={selectedCustomer?.toString()}
                  onValueChange={(v) => setSelectedCustomer(parseInt(v))}
                >
                  <SelectTrigger className="h-8 flex-1">
                    <SelectValue placeholder="Seleccionar cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem
                        key={customer.id}
                        value={customer.id.toString()}
                      >
                        {customer.name} - {customer.phone}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowNewCustomerModal(true)}
                  className="h-8"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Customer Points Info */}
              {selectedCustomer && (
                <div className="space-y-2 rounded-lg border border-purple-200 bg-purple-50 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-purple-900">
                      Puntos Disponibles:
                    </span>
                    <span className="text-sm font-bold text-purple-700">
                      {customerPoints.toLocaleString()} pts
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-green-700">
                        Puntos a Ganar:
                      </span>
                      <span className="text-sm font-bold text-green-600">
                        +{pointsEarned} pts
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-green-600 italic">
                      1 punto = ${POINT_VALUE.toFixed(2)}
                    </p>
                  </div>

                  {/* Use Points */}
                  {customerPoints > 0 && (
                    <div className="space-y-2 border-t border-purple-200 pt-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="use-points"
                          checked={usePoints}
                          onCheckedChange={(checked) => {
                            setUsePoints(checked as boolean)
                            if (!checked) setPointsToUse(0)
                          }}
                        />
                        <label
                          htmlFor="use-points"
                          className="cursor-pointer text-xs font-medium text-purple-900"
                        >
                          Usar puntos en esta compra
                        </label>
                      </div>

                      {usePoints && (
                        <div className="space-y-1">
                          <label className="text-xs text-purple-700">
                            Puntos a usar:
                          </label>
                          <Input
                            type="number"
                            min={0}
                            max={Math.min(customerPoints, totalBeforePoints)}
                            value={pointsToUse}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0
                              setPointsToUse(
                                Math.min(
                                  value,
                                  customerPoints,
                                  totalBeforePoints,
                                ),
                              )
                            }}
                            className="h-8 text-sm"
                            placeholder="0"
                          />
                          <p className="text-xs text-purple-600">
                            Máximo:{' '}
                            {Math.min(customerPoints, totalBeforePoints)} pts ($
                            {Math.min(
                              customerPoints,
                              totalBeforePoints,
                            ).toFixed(2)}
                            )
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Cart Items */}
        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {cart.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-slate-500">El carrito está vacío</p>
            </div>
          ) : (
            <>
              {cart.some((item) => item.selected) && (
                <Button
                  onClick={deleteSelectedItems}
                  variant="destructive"
                  size="sm"
                  className="w-full"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Eliminar Seleccionados
                </Button>
              )}

              {cart.map((item) => (
                <div key={item.id} className="rounded-lg bg-slate-50 p-3">
                  <div className="mb-2 flex items-start gap-3">
                    <Checkbox
                      checked={item.selected}
                      onCheckedChange={() => toggleSelectItem(item.id)}
                    />

                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-12 w-12 rounded object-cover"
                    />

                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-slate-900">
                        {item.name}
                      </h4>
                      <p className="text-xs text-slate-500">{item.code}</p>
                      <p className="text-xs text-slate-600">{item.category}</p>
                      <p className="mt-0.5 text-xs text-slate-600">
                        ${item.price.toFixed(2)} c/u
                      </p>

                      {/* Show promotion/discount */}
                      {item.promotion && (
                        <div className="mt-1">
                          <Badge className="bg-purple-100 text-xs text-purple-700">
                            {item.promotion}
                          </Badge>
                          {item.promotionExpiry && (
                            <p className="mt-0.5 text-xs text-slate-500">
                              Válido:{' '}
                              {new Date(
                                item.promotionExpiry,
                              ).toLocaleDateString('es-MX')}
                            </p>
                          )}
                        </div>
                      )}
                      {item.promotionDiscount && (
                        <div className="mt-1">
                          <Badge className="bg-orange-100 text-xs text-orange-700">
                            {item.promotionDiscount}% descuento
                          </Badge>
                          {item.promotionExpiry && (
                            <p className="mt-0.5 text-xs text-slate-500">
                              Válido:{' '}
                              {new Date(
                                item.promotionExpiry,
                              ).toLocaleDateString('es-MX')}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.id, -1)}
                        className="flex h-6 w-6 items-center justify-center rounded border border-slate-200 bg-white hover:bg-slate-100"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="w-8 text-center text-sm font-medium">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, 1)}
                        className="flex h-6 w-6 items-center justify-center rounded border border-slate-200 bg-white hover:bg-slate-100"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>

                    <div className="text-right">
                      {item.promotionDiscount && (
                        <p className="text-xs text-slate-500 line-through">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                      )}
                      <p className="text-sm font-semibold text-slate-900">
                        $
                        {(
                          item.price *
                          (item.promotionDiscount
                            ? 1 - item.promotionDiscount / 100
                            : 1) *
                          item.quantity
                        ).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Totals */}
        {cart.length > 0 && (
          <div className="space-y-3 border-t border-slate-200 p-4">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Subtotal:</span>
              <span className="font-medium text-slate-900">
                ${subtotal.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">IVA (16%):</span>
              <span className="font-medium text-slate-900">
                ${tax.toFixed(2)}
              </span>
            </div>
            {saleType === 'delivery' && deliveryCost > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Costo de Envío:</span>
                <span className="font-medium text-slate-900">
                  ${deliveryCost.toFixed(2)}
                </span>
              </div>
            )}
            {pointsDiscount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-purple-600">Descuento por Puntos:</span>
                <span className="font-medium text-purple-600">
                  -${pointsDiscount.toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex justify-between border-t border-slate-200 pt-3">
              <span className="font-semibold text-slate-900">Total:</span>
              <span className="text-xl font-semibold text-slate-900">
                ${total.toFixed(2)}
              </span>
            </div>
            {customerType === 'registered' && selectedCustomer && (
              <div className="mt-2 rounded-lg border border-green-200 bg-green-50 p-2">
                <p className="text-xs text-green-800">
                  ✓ Cliente ganará{' '}
                  <span className="font-bold">{pointsEarned} puntos</span> con
                  esta compra
                </p>
              </div>
            )}

            <Button
              onClick={() => setShowPaymentModal(true)}
              className="w-full"
              size="lg"
              disabled={cashLimit}
            >
              <DollarSign className="mr-2 h-5 w-5" />
              Procesar Pago
            </Button>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Procesar Pago</DialogTitle>
            <DialogDescription className="sr-only">
              Seleccione el método de pago y complete la transacción
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-slate-50 p-4">
              <div className="mb-1 flex justify-between">
                <span className="text-sm text-slate-600">Total a pagar:</span>
                <span className="text-2xl font-semibold text-slate-900">
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">
                Método de pago:
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setSelectedPaymentMethod('cash')}
                  className={`rounded-lg border-2 p-4 transition-all ${
                    selectedPaymentMethod === 'cash'
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Banknote className="mx-auto mb-2 h-6 w-6" />
                  <p className="text-xs font-medium">Efectivo</p>
                </button>
                <button
                  onClick={() => setSelectedPaymentMethod('card')}
                  className={`rounded-lg border-2 p-4 transition-all ${
                    selectedPaymentMethod === 'card'
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <CreditCard className="mx-auto mb-2 h-6 w-6" />
                  <p className="text-xs font-medium">Tarjeta</p>
                </button>
                <button
                  onClick={() => setSelectedPaymentMethod('transfer')}
                  className={`rounded-lg border-2 p-4 transition-all ${
                    selectedPaymentMethod === 'transfer'
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <DollarSign className="mx-auto mb-2 h-6 w-6" />
                  <p className="text-xs font-medium">Transferencia</p>
                </button>
                <button
                  onClick={() => setSelectedPaymentMethod('credit')}
                  className={`rounded-lg border-2 p-4 transition-all ${
                    selectedPaymentMethod === 'credit'
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Calendar className="mx-auto mb-2 h-6 w-6" />
                  <p className="text-xs font-medium">Crédito</p>
                </button>
              </div>
            </div>

            {selectedPaymentMethod === 'cash' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Efectivo recibido:
                </label>
                <Input
                  type="number"
                  value={cashReceived}
                  onChange={(e) => setCashReceived(e.target.value)}
                  placeholder="0.00"
                />
                {parseFloat(cashReceived || '0') >= total && (
                  <div className="rounded-lg bg-green-50 p-3">
                    <p className="text-sm text-green-700">
                      Cambio:{' '}
                      <span className="font-semibold">
                        ${change.toFixed(2)}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            )}

            {selectedPaymentMethod === 'card' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Banco de la tarjeta:
                </label>
                <Select
                  value={selectedCardBank}
                  onValueChange={setSelectedCardBank}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar banco" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BBVA">BBVA</SelectItem>
                    <SelectItem value="BANAMEX">Banamex</SelectItem>
                    <SelectItem value="SANTANDER">Santander</SelectItem>
                    <SelectItem value="HSBC">HSBC</SelectItem>
                    <SelectItem value="BANORTE">Banorte</SelectItem>
                    <SelectItem value="SCOTIABANK">Scotiabank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedPaymentMethod === 'transfer' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Banco de la transferencia:
                </label>
                <Select value={selectedBank} onValueChange={setSelectedBank}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar banco" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BBVA">BBVA</SelectItem>
                    <SelectItem value="BANAMEX">Banamex</SelectItem>
                    <SelectItem value="SANTANDER">Santander</SelectItem>
                    <SelectItem value="HSBC">HSBC</SelectItem>
                    <SelectItem value="MP">Mercado Pago</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedPaymentMethod === 'credit' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Plazo (días):
                  </label>
                  <Input
                    type="number"
                    value={creditTermDays}
                    onChange={(e) => setCreditTermDays(e.target.value)}
                    placeholder="30"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Número de pagos:
                  </label>
                  <Input
                    type="number"
                    value={creditPayments}
                    onChange={(e) => setCreditPayments(e.target.value)}
                    placeholder="3"
                  />
                </div>
              </div>
            )}

            {/* Invoice and tickets */}
            <div className="space-y-3 border-t border-slate-200 pt-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">
                  ¿Requiere factura?
                </label>
                <Checkbox
                  checked={requiresInvoice}
                  onCheckedChange={(checked) =>
                    setRequiresInvoice(checked as boolean)
                  }
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Cantidad de tickets a imprimir:
                </label>
                <Input
                  type="number"
                  min="1"
                  value={ticketCopies}
                  onChange={(e) =>
                    setTicketCopies(parseInt(e.target.value) || 1)
                  }
                />
              </div>
            </div>

            <Button
              onClick={proceedToPayment}
              disabled={
                !selectedPaymentMethod ||
                (selectedPaymentMethod === 'cash' &&
                  parseFloat(cashReceived || '0') < total) ||
                (selectedPaymentMethod === 'card' && !selectedCardBank) ||
                (selectedPaymentMethod === 'transfer' && !selectedBank) ||
                (selectedPaymentMethod === 'credit' &&
                  (!creditTermDays || !creditPayments))
              }
              className="w-full"
              size="lg"
            >
              Continuar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket Preview Modal */}
      <Dialog open={showTicketPreview} onOpenChange={setShowTicketPreview}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Vista Previa del Ticket</DialogTitle>
            <DialogDescription className="sr-only">
              Vista previa del ticket de venta
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Ticket */}
            <div className="rounded-lg border-2 border-dashed border-slate-300 bg-white p-6 font-mono text-sm">
              <div className="mb-4 text-center">
                <h3 className="text-base font-bold">Farmacias Salud Plus</h3>
                <p className="text-xs">Sucursal Centro</p>
                <p className="text-xs">Av. Principal #123</p>
                <p className="text-xs">Tel: 555-1234</p>
              </div>

              <div className="mb-2 border-t border-dashed border-slate-300 pt-2">
                <p className="text-xs">
                  Ticket: <span className="font-bold">{ticketNumber}</span>
                </p>
                <p className="text-xs">Caja: #{cashierNumber}</p>
                <p className="text-xs">Cajero: {cashierName}</p>
                <p className="text-xs">Fecha: {new Date().toLocaleString()}</p>
                {customerType === 'registered' && selectedCustomer && (
                  <p className="text-xs">
                    Cliente:{' '}
                    {customers.find((c) => c.id === selectedCustomer)?.name}
                  </p>
                )}
                {saleType === 'delivery' && deliveryDetails.deliveryPerson && (
                  <>
                    <p className="mt-1 text-xs font-bold">
                      ENTREGA A DOMICILIO
                    </p>
                    <p className="text-xs">
                      Repartidor: {deliveryDetails.deliveryPerson}
                    </p>
                    <p className="text-xs">
                      Tel: {deliveryDetails.customerPhone}
                    </p>
                    <p className="text-xs">
                      Dir: {deliveryDetails.customerAddress}
                    </p>
                    {deliveryDetails.observations && (
                      <p className="text-xs">
                        Obs: {deliveryDetails.observations}
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="mb-2 border-t border-dashed border-slate-300 pt-2">
                {cart.map((item) => {
                  const itemPrice = item.promotionDiscount
                    ? item.price * (1 - item.promotionDiscount / 100)
                    : item.price
                  return (
                    <div key={item.id} className="mb-2">
                      <p className="text-xs font-bold">{item.name}</p>
                      <div className="flex justify-between text-xs">
                        <span>
                          {item.quantity} x ${itemPrice.toFixed(2)}
                        </span>
                        <span>${(itemPrice * item.quantity).toFixed(2)}</span>
                      </div>
                      {item.promotionDiscount && (
                        <>
                          <p className="text-xs text-orange-600">
                            Desc. {item.promotionDiscount}%
                          </p>
                          {item.promotionExpiry && (
                            <p className="text-xs text-slate-500">
                              Válido:{' '}
                              {new Date(
                                item.promotionExpiry,
                              ).toLocaleDateString('es-MX')}
                            </p>
                          )}
                        </>
                      )}
                      {item.promotion && (
                        <>
                          <p className="text-xs text-purple-600">
                            {item.promotion}
                          </p>
                          {item.promotionExpiry && (
                            <p className="text-xs text-slate-500">
                              Válido:{' '}
                              {new Date(
                                item.promotionExpiry,
                              ).toLocaleDateString('es-MX')}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )
                })}
              </div>

              <div className="space-y-1 border-t border-dashed border-slate-300 pt-2">
                <div className="flex justify-between text-xs">
                  <span>Subtotal:</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span>IVA (16%):</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
                {saleType === 'delivery' && deliveryCost > 0 && (
                  <div className="flex justify-between text-xs">
                    <span>Costo de Envío:</span>
                    <span>${deliveryCost.toFixed(2)}</span>
                  </div>
                )}
                {pointsDiscount > 0 && (
                  <div className="flex justify-between text-xs">
                    <span>Desc. Puntos:</span>
                    <span>-${pointsDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-slate-300 pt-1 text-sm font-bold">
                  <span>TOTAL:</span>
                  <span>${total.toFixed(2)}</span>
                </div>
                {customerType === 'registered' && selectedCustomer && (
                  <div className="mt-2 border-t border-slate-300 pt-2 text-xs">
                    <div className="flex justify-between">
                      <span>Puntos ganados:</span>
                      <span className="font-bold">+{pointsEarned} pts</span>
                    </div>
                    {pointsDiscount > 0 && (
                      <div className="flex justify-between">
                        <span>Puntos usados:</span>
                        <span className="font-bold">-{pointsToUse} pts</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-2 flex justify-between text-xs">
                  <span>Método de pago:</span>
                  <span className="uppercase">{selectedPaymentMethod}</span>
                </div>

                {selectedPaymentMethod === 'card' && selectedCardBank && (
                  <div className="flex justify-between text-xs">
                    <span>Banco:</span>
                    <span>{selectedCardBank}</span>
                  </div>
                )}

                {selectedPaymentMethod === 'cash' && (
                  <>
                    <div className="flex justify-between text-xs">
                      <span>Efectivo:</span>
                      <span>${parseFloat(cashReceived).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Cambio:</span>
                      <span>${change.toFixed(2)}</span>
                    </div>
                  </>
                )}

                {selectedPaymentMethod === 'credit' && (
                  <>
                    <div className="flex justify-between text-xs">
                      <span>Plazo:</span>
                      <span>{creditTermDays} días</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Pagos:</span>
                      <span>{creditPayments}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="mt-4 text-center text-xs">
                <p>¡Gracias por su compra!</p>
                {requiresInvoice && (
                  <p className="mt-1 font-bold">REQUIERE FACTURA</p>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={completeSale} className="flex-1">
                <Printer className="mr-2 h-4 w-4" />
                Imprimir y Finalizar
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowTicketPreview(false)
                  setShowPaymentModal(true)
                }}
              >
                Editar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent
          className="sm:max-w-sm"
          aria-describedby="success-description"
        >
          <div className="py-6 text-center" id="success-description">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="mb-2 text-xl font-semibold text-slate-900">
              ¡Venta Completada!
            </h3>
            <p className="text-sm text-slate-600">Ticket #{ticketNumber}</p>
            <p className="mt-4 text-2xl font-semibold text-slate-900">
              ${total.toFixed(2)}
            </p>
            {requiresInvoice && (
              <p className="mt-2 text-sm text-blue-600">Requiere factura</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cash Withdrawal Modal */}
      <Dialog open={showCashWithdrawal} onOpenChange={setShowCashWithdrawal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Retiro de Efectivo</DialogTitle>
            <DialogDescription className="sr-only">
              Retirar efectivo de la caja
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-slate-50 p-4">
              <p className="text-sm text-slate-600">Efectivo en caja:</p>
              <p className="text-2xl font-semibold text-slate-900">
                ${currentCashInRegister.toFixed(2)} MXN
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Monto a retirar: <span className="text-red-600">*</span>
              </label>
              <Input
                type="number"
                value={withdrawalAmount}
                onChange={(e) => setWithdrawalAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Concepto: <span className="text-red-600">*</span>
              </label>
              <Input
                value={withdrawalConcept}
                onChange={(e) => setWithdrawalConcept(e.target.value)}
                placeholder="Ej: Pago a proveedor, gastos varios..."
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleCashWithdrawal}
                disabled={
                  !withdrawalAmount ||
                  parseFloat(withdrawalAmount) <= 0 ||
                  parseFloat(withdrawalAmount) > currentCashInRegister ||
                  !withdrawalConcept.trim()
                }
                className="flex-1"
              >
                Confirmar Retiro
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCashWithdrawal(false)
                  setWithdrawalAmount('')
                  setWithdrawalConcept('')
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cash Deposit Modal */}
      <Dialog open={showCashDeposit} onOpenChange={setShowCashDeposit}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ingreso de Efectivo</DialogTitle>
            <DialogDescription className="sr-only">
              Ingresar efectivo a la caja
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-slate-50 p-4">
              <p className="text-sm text-slate-600">Efectivo actual en caja:</p>
              <p className="text-2xl font-semibold text-slate-900">
                ${currentCashInRegister.toFixed(2)} MXN
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Monto a ingresar: <span className="text-red-600">*</span>
              </label>
              <Input
                type="number"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Concepto: <span className="text-red-600">*</span>
              </label>
              <Input
                value={depositConcept}
                onChange={(e) => setDepositConcept(e.target.value)}
                placeholder="Ej: Fondo de caja, reposición..."
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleCashDeposit}
                disabled={
                  !depositAmount ||
                  parseFloat(depositAmount) <= 0 ||
                  !depositConcept.trim()
                }
                className="flex-1"
              >
                Confirmar Ingreso
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCashDeposit(false)
                  setDepositAmount('')
                  setDepositConcept('')
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cash Count Modal */}
      <Dialog open={showCashCount} onOpenChange={setShowCashCount}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Corte de Caja</DialogTitle>
            <DialogDescription className="sr-only">
              Realizar corte de caja
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="mb-2 text-sm font-medium text-blue-900">
                Calculadora de Denominaciones:
              </p>
              <p className="text-xs text-blue-800">
                Ingresa la cantidad de monedas y billetes que tienes de cada
                denominación
              </p>
            </div>

            {/* Tabla de denominaciones */}
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase">
                      Denominación
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase">
                      Cantidad
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {[1000, 500, 200, 100, 50, 20, 10, 5, 2, 1].map((denom) => (
                    <tr key={denom} className="hover:bg-slate-50">
                      <td className="px-4 py-2 font-medium text-slate-900">
                        ${denom}
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          type="number"
                          min="0"
                          value={
                            denominations[denom as keyof typeof denominations]
                          }
                          onChange={(e) =>
                            handleDenominationChange(
                              denom,
                              parseInt(e.target.value) || 0,
                            )
                          }
                          className="h-8 w-24"
                        />
                      </td>
                      <td className="px-4 py-2 font-semibold text-slate-900">
                        $
                        {(
                          denom *
                          (denominations[denom as keyof typeof denominations] ||
                            0)
                        ).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Efectivo contado (Total):
              </label>
              <Input
                type="number"
                value={countedCash}
                onChange={(e) => setCountedCash(e.target.value)}
                placeholder="0.00"
                className="bg-slate-50 text-lg font-bold"
                readOnly
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleCashCount}
                disabled={!countedCash}
                className="flex-1"
              >
                Confirmar Corte
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCashCount(false)
                  setCountedCash('')
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cash Count Result Modal */}
      <Dialog open={showCashCountResult} onOpenChange={setShowCashCountResult}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Desglose de Corte de Caja</DialogTitle>
            <DialogDescription className="sr-only">
              Resultado del corte de caja
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Status indicator */}
            <div
              className={`rounded-lg p-4 ${
                cashCountStatus === 'success'
                  ? 'border border-green-200 bg-green-50'
                  : cashCountStatus === 'surplus'
                    ? 'border border-yellow-200 bg-yellow-50'
                    : 'border border-red-200 bg-red-50'
              }`}
            >
              <div className="flex items-center gap-3">
                {cashCountStatus === 'success' && (
                  <>
                    <Check className="h-8 w-8 text-green-600" />
                    <div>
                      <h3 className="font-semibold text-green-900">
                        ¡Corte Exitoso!
                      </h3>
                      <p className="text-sm text-green-700">
                        El efectivo coincide perfectamente
                      </p>
                    </div>
                  </>
                )}
                {cashCountStatus === 'surplus' && (
                  <>
                    <AlertCircle className="h-8 w-8 text-yellow-600" />
                    <div>
                      <h3 className="font-semibold text-yellow-900">
                        Sobrante Detectado
                      </h3>
                      <p className="text-sm text-yellow-700">
                        Hay más efectivo del registrado
                      </p>
                    </div>
                  </>
                )}
                {cashCountStatus === 'shortage' && (
                  <>
                    <AlertCircle className="h-8 w-8 text-red-600" />
                    <div>
                      <h3 className="font-semibold text-red-900">
                        Faltante Detectado
                      </h3>
                      <p className="text-sm text-red-700">
                        Hay menos efectivo del registrado
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Desglose */}
            <div className="rounded-lg border-2 border-dashed border-slate-300 bg-white p-6 font-mono text-sm">
              <div className="mb-4 text-center">
                <h3 className="text-base font-bold">Farmacias Salud Plus</h3>
                <p className="text-xs">TICKET DE CORTE DE CAJA</p>
              </div>

              <div className="mb-3 border-t border-dashed border-slate-300 pt-2">
                <p className="text-xs">Caja: #{cashierNumber}</p>
                <p className="text-xs">Cajero: {cashierName}</p>
                <p className="text-xs">Fecha: {new Date().toLocaleString()}</p>
              </div>

              <div className="space-y-2 border-t border-dashed border-slate-300 pt-3">
                <h4 className="mb-2 text-xs font-bold text-slate-900">
                  RESUMEN DE VENTAS
                </h4>
                <div className="flex justify-between text-xs">
                  <span>Total de ventas:</span>
                  <span className="font-semibold">$12,450.00</span>
                </div>
                <div className="space-y-1 rounded bg-slate-50 p-2 pl-2 text-xs">
                  <div className="flex justify-between">
                    <span>Efectivo:</span>
                    <span>$6,200.00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tarjeta:</span>
                    <span>$4,150.00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Transferencia:</span>
                    <span>$2,100.00</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2 border-t border-dashed border-slate-300 pt-3">
                <h4 className="mb-2 text-xs font-bold text-slate-900">
                  MOVIMIENTOS DE CAJA
                </h4>
                <div className="flex justify-between text-xs">
                  <span>Total ingresos:</span>
                  <span className="font-semibold text-green-600">
                    $7,500.00
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span>Total egresos:</span>
                  <span className="font-semibold text-red-600">$1,000.00</span>
                </div>
              </div>

              <div className="space-y-2 border-t border-dashed border-slate-300 pt-3">
                <div className="flex justify-between text-sm">
                  <span>Efectivo en sistema:</span>
                  <span className="font-semibold">
                    ${currentCashInRegister.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Efectivo contado:</span>
                  <span className="font-semibold">
                    ${parseFloat(countedCash || '0').toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Desglose de Denominaciones */}
              <div className="space-y-2 border-t border-dashed border-slate-300 pt-3">
                <h4 className="mb-2 text-xs font-bold text-slate-900">
                  DESGLOSE DE EFECTIVO
                </h4>
                <div className="space-y-1 rounded bg-slate-50 p-2 pl-2 text-xs">
                  {Object.entries(denominations)
                    .sort(([a], [b]) => parseFloat(b) - parseFloat(a))
                    .filter(([_, qty]) => qty > 0)
                    .map(([denom, qty]) => (
                      <div key={denom} className="flex justify-between">
                        <span>
                          ${denom} x {qty}:
                        </span>
                        <span>
                          ${(parseFloat(denom) * qty).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  {Object.values(denominations).every((v) => v === 0) && (
                    <p className="text-xs text-slate-500 italic">
                      Sin desglose de denominaciones
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-3 border-t border-dashed border-slate-300 pt-3">
                <div
                  className={`flex justify-between text-base font-bold ${
                    cashCountStatus === 'success'
                      ? 'text-green-600'
                      : cashCountStatus === 'surplus'
                        ? 'text-yellow-600'
                        : 'text-red-600'
                  }`}
                >
                  <span>
                    {cashCountStatus === 'success'
                      ? 'CORTE EXITOSO'
                      : cashCountStatus === 'surplus'
                        ? 'SOBRANTE:'
                        : 'FALTANTE:'}
                  </span>
                  <span>
                    {cashCountStatus === 'success'
                      ? '$0.00'
                      : `$${Math.abs(cashCountDifference).toFixed(2)}`}
                  </span>
                </div>
              </div>

              <div className="mt-4 border-t border-dashed border-slate-300 pt-3 text-center text-xs">
                <p>Firma del cajero</p>
                <div className="mt-6 mb-2 border-b border-slate-400"></div>
                <p className="mt-4">Firma del supervisor</p>
                <div className="mt-6 mb-2 border-b border-slate-400"></div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => {
                  window.print()
                }}
                variant="outline"
                className="flex-1"
              >
                <Printer className="mr-2 h-4 w-4" />
                Imprimir Ticket
              </Button>
              <Button onClick={closeCashCountResult} className="flex-1">
                Cerrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Cashier Modal */}
      <Dialog open={showChangeCashier} onOpenChange={setShowChangeCashier}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cambiar Caja</DialogTitle>
            <DialogDescription className="sr-only">
              Cambiar número de caja con PIN de administrador
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <p className="text-sm text-yellow-900">
                Se requiere PIN de administrador para cambiar de caja
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                PIN de Administrador:
              </label>
              <Input
                type="password"
                value={adminPin}
                onChange={(e) => setAdminPin(e.target.value)}
                placeholder="••••"
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleChangeCashier}
                disabled={!adminPin}
                className="flex-1"
              >
                Confirmar
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowChangeCashier(false)
                  setAdminPin('')
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Prescription Modal */}
      <Dialog
        open={showPrescriptionModal}
        onOpenChange={setShowPrescriptionModal}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Información de Receta Médica</DialogTitle>
            <DialogDescription className="sr-only">
              Captura de información requerida para productos controlados y
              antibióticos
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {currentPrescriptionProduct &&
              (() => {
                const product = products.find(
                  (p) => p.id === currentPrescriptionProduct,
                )
                return (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                    <p className="text-sm font-medium text-red-900">
                      Este producto requiere receta médica. Complete la
                      siguiente información.
                    </p>
                    {product && (
                      <p className="mt-1 text-xs text-red-700">
                        Tipo:{' '}
                        {product.productType === 'controlled'
                          ? 'Medicamento Controlado'
                          : 'Medicamento Antibiótico'}
                      </p>
                    )}
                  </div>
                )
              })()}

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Médico <span className="text-red-600">*</span>
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 space-y-2">
                    <Input
                      placeholder="Buscar por nombre o cédula..."
                      value={doctorSearch}
                      onChange={(e) => setDoctorSearch(e.target.value)}
                    />
                    {doctorSearch && (
                      <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                        {doctors
                          .filter(
                            (d) =>
                              d.name
                                .toLowerCase()
                                .includes(doctorSearch.toLowerCase()) ||
                              d.professionalId.includes(doctorSearch),
                          )
                          .map((doctor) => (
                            <button
                              key={doctor.id}
                              type="button"
                              onClick={() => {
                                setSelectedDoctor(doctor.id)
                                setDoctorSearch(doctor.name)
                                const nameInput = document.getElementById(
                                  'doctorName',
                                ) as HTMLInputElement
                                const addressInput = document.getElementById(
                                  'doctorAddress',
                                ) as HTMLInputElement
                                const idInput = document.getElementById(
                                  'professionalId',
                                ) as HTMLInputElement
                                if (nameInput) nameInput.value = doctor.name
                                if (addressInput)
                                  addressInput.value = doctor.address
                                if (idInput)
                                  idInput.value = doctor.professionalId
                              }}
                              className="w-full border-b border-slate-100 px-3 py-2 text-left last:border-0 hover:bg-slate-50"
                            >
                              <p className="text-sm font-medium text-slate-900">
                                {doctor.name}
                              </p>
                              <p className="text-xs text-slate-600">
                                Cédula: {doctor.professionalId}
                              </p>
                              <p className="text-xs text-slate-500">
                                {doctor.specialty}
                              </p>
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowNewDoctorModal(true)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="col-span-2 space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Nombre del Médico <span className="text-red-600">*</span>
                </label>
                <Input id="doctorName" placeholder="Dr. Juan Pérez" />
              </div>

              <div className="col-span-2 space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Dirección del Médico <span className="text-red-600">*</span>
                </label>
                <Input id="doctorAddress" placeholder="Calle Principal #123" />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Cédula Profesional <span className="text-red-600">*</span>
                </label>
                <Input id="professionalId" placeholder="1234567" />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Fecha de Receta <span className="text-red-600">*</span>
                </label>
                <Input id="prescriptionDate" type="date" />
              </div>

              <div className="col-span-2 space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Fecha de Venta <span className="text-red-600">*</span>
                </label>
                <Input
                  id="saleDate"
                  type="date"
                  defaultValue={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="col-span-2 space-y-3">
                <label className="text-sm font-medium text-slate-700">
                  Surtido de Receta <span className="text-red-600">*</span>
                </label>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="fulfillmentPartial"
                      name="fulfillment"
                      onCheckedChange={(checked) => {
                        if (checked) {
                          const completeCheck = document.getElementById(
                            'fulfillmentComplete',
                          ) as HTMLInputElement
                          if (completeCheck) completeCheck.checked = false
                          handleFulfillmentTypeChange(false)
                        }
                      }}
                    />
                    <label
                      htmlFor="fulfillmentPartial"
                      className="text-sm text-slate-700"
                    >
                      Surtido Parcial
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="fulfillmentComplete"
                      name="fulfillment"
                      onCheckedChange={(checked) => {
                        if (checked) {
                          const partialCheck = document.getElementById(
                            'fulfillmentPartial',
                          ) as HTMLInputElement
                          if (partialCheck) partialCheck.checked = false
                          handleFulfillmentTypeChange(true)
                        }
                      }}
                    />
                    <label
                      htmlFor="fulfillmentComplete"
                      className="text-sm text-slate-700"
                    >
                      Surtido Completo
                    </label>
                  </div>
                </div>
                <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <p className="text-xs text-blue-900">
                    <strong>Nota:</strong> Para antibióticos el folio se genera
                    automáticamente. Para medicamentos controlados debe
                    ingresarse manualmente.
                  </p>
                </div>
              </div>

              {/* Folio para Antibióticos (Automático) */}
              {generatedFolio &&
                currentPrescriptionProduct &&
                (() => {
                  const product = products.find(
                    (p) => p.id === currentPrescriptionProduct,
                  )
                  return (
                    product?.productType === 'antibiotic' && (
                      <div className="col-span-2 space-y-2">
                        <label className="text-sm font-medium text-slate-700">
                          Folio Consecutivo{' '}
                          <span className="text-green-600">✓</span>
                        </label>
                        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-green-900">
                                Folio generado automáticamente:
                              </p>
                              <p className="mt-1 font-mono text-xl font-bold text-green-700">
                                {generatedFolio}
                              </p>
                            </div>
                            <Check className="h-8 w-8 text-green-600" />
                          </div>
                          <p className="mt-2 text-xs text-green-700">
                            MA = Medicamento Antibiótico
                          </p>
                        </div>
                      </div>
                    )
                  )
                })()}

              {/* Folio para Medicamentos Controlados (Manual) */}
              {currentPrescriptionProduct &&
                (() => {
                  const product = products.find(
                    (p) => p.id === currentPrescriptionProduct,
                  )
                  const isComplete = (
                    document.getElementById(
                      'fulfillmentComplete',
                    ) as HTMLInputElement
                  )?.checked
                  return (
                    product?.productType === 'controlled' &&
                    isComplete && (
                      <div className="col-span-2 space-y-2">
                        <label className="text-sm font-medium text-slate-700">
                          Folio Consecutivo{' '}
                          <span className="text-red-600">*</span>
                        </label>
                        <Input
                          type="text"
                          placeholder="Ingrese folio manualmente"
                          value={manualFolio}
                          onChange={(e) => setManualFolio(e.target.value)}
                        />
                        <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                          <p className="text-xs text-orange-900">
                            <strong>Medicamento Controlado:</strong> El folio
                            debe ingresarse manualmente.
                          </p>
                        </div>
                      </div>
                    )
                  )
                })()}
            </div>

            <div className="flex gap-2">
              <Button onClick={savePrescriptionData} className="flex-1">
                <Check className="mr-2 h-4 w-4" />
                Guardar y Agregar al Carrito
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowPrescriptionModal(false)
                  setCurrentPrescriptionProduct(null)
                  setGeneratedFolio('')
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Customer Modal - Quick Registration from POS */}
      <Dialog
        open={showNewCustomerModal}
        onOpenChange={setShowNewCustomerModal}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Registro Rápido de Cliente</DialogTitle>
            <DialogDescription className="sr-only">
              Formulario rápido para registrar un nuevo cliente desde el punto
              de venta
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <p className="text-sm text-blue-900">
                Registra un nuevo cliente para poder asignarle puntos y llevar
                un historial de compras.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Nombre Completo <span className="text-red-600">*</span>
              </label>
              <Input placeholder="Ej: Juan Pérez García" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Teléfono <span className="text-red-600">*</span>
                </label>
                <Input placeholder="555-1234" />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Email
                </label>
                <Input type="email" placeholder="correo@ejemplo.com" />
              </div>
            </div>

            <div className="rounded-lg bg-slate-50 p-3">
              <p className="mb-2 text-xs text-slate-600">
                Dirección (Opcional)
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Calle" className="h-8 text-sm" />
                <Input placeholder="Número" className="h-8 text-sm" />
                <Input placeholder="Colonia" className="h-8 text-sm" />
                <Input placeholder="Código Postal" className="h-8 text-sm" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Comentarios
              </label>
              <Input placeholder="Notas adicionales..." />
            </div>

            <div className="flex gap-3 border-t border-slate-200 pt-4">
              <Button
                onClick={() => {
                  console.log('Cliente registrado desde POS')
                  setShowNewCustomerModal(false)
                }}
                className="flex-1"
              >
                <Check className="mr-2 h-4 w-4" />
                Guardar y Seleccionar
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowNewCustomerModal(false)}
                className="flex-1"
              >
                <X className="mr-2 h-4 w-4" />
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delivery Details Modal */}
      <Dialog
        open={showDeliveryDetailsModal}
        onOpenChange={setShowDeliveryDetailsModal}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Datos de Entrega a Domicilio</DialogTitle>
            <DialogDescription className="sr-only">
              Formulario para capturar detalles de la entrega a domicilio
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Repartidor <span className="text-red-600">*</span>
              </label>
              <Select
                value={deliveryDetails.deliveryPerson}
                onValueChange={(value) =>
                  setDeliveryDetails({
                    ...deliveryDetails,
                    deliveryPerson: value,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar repartidor" />
                </SelectTrigger>
                <SelectContent>
                  {deliveryPersons.map((person) => (
                    <SelectItem key={person.id} value={person.name}>
                      {person.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Teléfono del Cliente <span className="text-red-600">*</span>
              </label>
              <Input
                value={deliveryDetails.customerPhone}
                onChange={(e) =>
                  setDeliveryDetails({
                    ...deliveryDetails,
                    customerPhone: e.target.value,
                  })
                }
                placeholder="555-1234"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Domicilio de Entrega <span className="text-red-600">*</span>
              </label>
              <Textarea
                value={deliveryDetails.customerAddress}
                onChange={(e) =>
                  setDeliveryDetails({
                    ...deliveryDetails,
                    customerAddress: e.target.value,
                  })
                }
                placeholder="Calle, número, colonia, código postal..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Costo de Envío <span className="text-red-600">*</span>
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={deliveryDetails.deliveryCost}
                onChange={(e) =>
                  setDeliveryDetails({
                    ...deliveryDetails,
                    deliveryCost: parseFloat(e.target.value) || 0,
                  })
                }
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Observaciones
              </label>
              <Textarea
                value={deliveryDetails.observations}
                onChange={(e) =>
                  setDeliveryDetails({
                    ...deliveryDetails,
                    observations: e.target.value,
                  })
                }
                placeholder="Notas adicionales sobre la entrega..."
                rows={2}
              />
            </div>

            <div className="flex gap-2 border-t border-slate-200 pt-4">
              <Button
                onClick={() => {
                  if (
                    deliveryDetails.deliveryPerson &&
                    deliveryDetails.customerPhone &&
                    deliveryDetails.customerAddress
                  ) {
                    setShowDeliveryDetailsModal(false)
                  }
                }}
                disabled={
                  !deliveryDetails.deliveryPerson ||
                  !deliveryDetails.customerPhone ||
                  !deliveryDetails.customerAddress
                }
                className="flex-1"
              >
                <Check className="mr-2 h-4 w-4" />
                Guardar
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowDeliveryDetailsModal(false)}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Doctor Modal */}
      <Dialog open={showNewDoctorModal} onOpenChange={setShowNewDoctorModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar Nuevo Médico</DialogTitle>
            <DialogDescription className="sr-only">
              Formulario para registrar un nuevo médico
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Nombre Completo <span className="text-red-600">*</span>
              </label>
              <Input placeholder="Dr. Juan Pérez García" />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Cédula Profesional <span className="text-red-600">*</span>
              </label>
              <Input placeholder="1234567" />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Especialidad <span className="text-red-600">*</span>
              </label>
              <Input placeholder="Medicina General" />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Dirección <span className="text-red-600">*</span>
              </label>
              <Textarea placeholder="Consultorio, calle, número..." rows={2} />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => {
                  console.log('Médico registrado')
                  setShowNewDoctorModal(false)
                }}
                className="flex-1"
              >
                <Check className="mr-2 h-4 w-4" />
                Guardar Médico
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowNewDoctorModal(false)}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
